import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowRight, CheckCircle, ShoppingCart, Target, Clock, BookOpen, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kajabi purchase link
const KAJABI_PURCHASE_URL = "https://vittafinance.mykajabi.com/offers/yjhefFLY";

const MOCK_EXAM_TILES = [
  { 
    id: 'mock-exam-1', 
    title: 'Exam 1', 
    accent: 'Teal',
    description: 'Core CIRO Elements 1–8',
    details: '120 questions covering fundamental derivatives concepts',
    color: '#14b8a6',
    bgColor: '#f0fdfa'
  },
  { 
    id: 'mock-exam-2', 
    title: 'Exam 2', 
    accent: 'Blue',
    description: 'Strategy mechanics & conduct',
    details: '120 questions on trading strategies and professional conduct',
    color: '#3b82f6',
    bgColor: '#eff6ff'
  },
  { 
    id: 'mock-exam-3', 
    title: 'Exam 3', 
    accent: 'Amber',
    description: 'Hard mode — case studies & advanced Greeks',
    details: '120 advanced questions with complex scenarios',
    color: '#f59e0b',
    bgColor: '#fffbeb'
  },
  { 
    id: 'mock-exam-4', 
    title: 'Exam 4', 
    accent: 'Emerald',
    description: 'Numericals only — 120 calculations',
    details: '120 calculation-focused questions',
    color: '#10b981',
    bgColor: '#f0fdf4'
  },
  { 
    id: 'mock-exam-5', 
    title: 'Exam 5', 
    accent: 'Indigo',
    description: 'Final comprehensive — all elements',
    details: '120 questions covering all topics',
    color: '#6366f1',
    bgColor: '#eef2ff'
  },
];

const DerivativesMockExamPackSalesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token');
  
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const PRICE = 49.99;

  useEffect(() => {
    if (tokenFromUrl) {
      verifyAccess(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyAccess = async (accessToken, userEmail = '') => {
    setCheckingAccess(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        token: accessToken,
        ...(userEmail && { email: userEmail })
      });
      
      const response = await fetch(
        `${API_URL}/api/books/derivatives-mock-exam/check-access?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setHasAccess(true);
          setShowAccessForm(false);
          localStorage.setItem('derivatives_mock_access_token', accessToken);
          if (userEmail) {
            localStorage.setItem('derivatives_mock_access_email', userEmail);
          }
          setSearchParams({ token: accessToken });
        } else {
          setError('Invalid access code or email. Please check and try again.');
        }
      } else {
        setError('Unable to verify access. Please try again.');
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Connection error. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!token.trim()) {
      setError('Please enter your access code.');
      return;
    }
    verifyAccess(token.trim(), email.trim());
  };

  const handleMockExamClick = (examId) => {
    if (!hasAccess) return;
    const url = `/books/derivatives-mock-exams/${examId}?token=${tokenFromUrl || token}`;
    window.open(url, '_blank');
  };

  if (hasAccess) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        {/* Success Header */}
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
          padding: '120px 24px 60px',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}>
              <CheckCircle size={48} color="white" />
            </div>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              marginBottom: '16px'
            }}>
              Access Granted!
            </h1>
            <p style={{
              fontSize: '1.125rem',
              opacity: 0.95,
              marginBottom: '32px'
            }}>
              You now have access to all 5 Derivatives Mock Exams
            </p>
          </div>
        </div>

        {/* Mock Exams Grid */}
        <div style={{ padding: '60px 24px', maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '800',
            color: '#1e293b',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            Your Mock Exams
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {MOCK_EXAM_TILES.map((exam, idx) => (
              <div
                key={exam.id}
                onClick={() => handleMockExamClick(exam.id)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '28px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${exam.color}20`,
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
                  e.currentTarget.style.borderColor = exam.color;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = `${exam.color}20`;
                }}
              >
                {/* Accent Badge */}
                <div style={{
                  display: 'inline-block',
                  background: exam.bgColor,
                  color: exam.color,
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  marginBottom: '16px',
                  letterSpacing: '0.5px'
                }}>
                  {exam.accent}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    background: exam.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileText size={28} color={exam.color} />
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    {exam.title}
                  </h3>
                </div>

                <div style={{
                  fontSize: '0.938rem',
                  fontWeight: '600',
                  color: '#475569',
                  marginBottom: '8px'
                }}>
                  {exam.description}
                </div>
                
                <p style={{
                  fontSize: '0.875rem',
                  color: '#64748b',
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  {exam.details}
                </p>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '16px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#64748b'
                  }}>
                    120 Questions
                  </span>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: exam.color,
                    fontWeight: '600',
                    fontSize: '0.938rem'
                  }}>
                    Start Exam <ArrowRight size={18} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Derivatives Mock Exam Pack",
          "description": "5 comprehensive Derivatives mock exams with 600 practice questions. Test your readiness with exam-focused scenarios.",
          "provider": {
            "@type": "Organization",
            "name": "Vitta Finance Academy",
            "url": "https://www.vittafinance.ca"
          },
          "offers": {
            "@type": "Offer",
            "price": "49.99",
            "priceCurrency": "CAD",
            "availability": "https://schema.org/InStock",
            "url": "https://www.vittafinance.ca/books/derivatives-mock-exam-pack"
          },
          "educationalLevel": "Professional"
        })
      }} />
      
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        padding: '120px 24px 80px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            padding: '12px 24px',
            borderRadius: '100px',
            marginBottom: '24px',
            fontSize: '0.875rem',
            fontWeight: '600',
            letterSpacing: '0.5px'
          }}>
            DERIVATIVES EXAM PREPARATION
          </div>

          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: '900',
            marginBottom: '24px',
            lineHeight: '1.1'
          }}>
            Derivatives Mock Exam Pack
          </h1>

          <p style={{
            fontSize: '1.25rem',
            opacity: 0.95,
            marginBottom: '40px',
            lineHeight: '1.7',
            maxWidth: '800px',
            margin: '0 auto 40px'
          }}>
            Test your readiness with 5 complete Derivatives mock exams. 600 questions with comprehensive rationales.
          </p>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '24px',
            marginBottom: '48px'
          }}>
            <div style={{
              fontSize: '3rem',
              fontWeight: '900'
            }}>
              ${PRICE.toFixed(2)} CAD
            </div>
          </div>

          {!showAccessForm && (
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href={KAJABI_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'white',
                  color: '#8b5cf6',
                  padding: '18px 32px',
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  textDecoration: 'none',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <ShoppingCart size={22} />
                Purchase Pack
              </a>
              <button
                onClick={() => setShowAccessForm(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '18px 32px',
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <Lock size={22} />
                Already Purchased?
              </button>
            </div>
          )}

          {showAccessForm && (
            <div style={{
              maxWidth: '500px',
              margin: '0 auto',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '32px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '24px'
              }}>
                Enter Your Details
              </h3>
              <form onSubmit={handleAccessSubmit}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                  required
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontSize: '1rem',
                    marginBottom: '16px',
                    outline: 'none'
                  }}
                />
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Access Code"
                  required
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontSize: '1rem',
                    marginBottom: '16px',
                    outline: 'none'
                  }}
                />
                {error && (
                  <p style={{
                    color: '#fecaca',
                    fontSize: '0.875rem',
                    marginBottom: '16px'
                  }}>
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={checkingAccess}
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#8b5cf6',
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    border: 'none',
                    cursor: checkingAccess ? 'not-allowed' : 'pointer',
                    opacity: checkingAccess ? 0.7 : 1
                  }}
                >
                  {checkingAccess ? 'Verifying...' : 'Access Exams'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '800',
            color: '#1e293b',
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            What's Included
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                icon: <Target size={32} />,
                title: '600 Exam Questions',
                desc: '5 comprehensive exams, each with 120 carefully crafted questions'
              },
              {
                icon: <BookOpen size={32} />,
                title: 'Detailed Rationales',
                desc: 'In-depth explanations for every question to reinforce your understanding'
              },
              {
                icon: <Clock size={32} />,
                title: 'Practice & Exam Modes',
                desc: 'Study at your pace or simulate real exam conditions'
              },
              {
                icon: <CheckCircle size={32} />,
                title: 'Lifetime Access',
                desc: 'Unlimited access to all 5 mock exams - practice as much as you need'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                background: '#f8f9fa',
                padding: '32px',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  color: '#8b5cf6',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  color: '#64748b',
                  lineHeight: '1.6'
                }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Exam Breakdown Section */}
      <div style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '800',
            color: '#1e293b',
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            Exam Breakdown
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: '#64748b',
            textAlign: 'center',
            marginBottom: '60px',
            maxWidth: '700px',
            margin: '0 auto 60px'
          }}>
            Each exam is strategically designed to test different aspects of your Derivatives knowledge
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {MOCK_EXAM_TILES.map((exam, idx) => (
              <div
                key={exam.id}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '28px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: `2px solid ${exam.color}20`,
                  position: 'relative',
                  opacity: 0.7
                }}
              >
                {/* Lock Icon Overlay */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(100, 116, 139, 0.1)',
                  borderRadius: '50%',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={20} color="#64748b" />
                </div>

                {/* Accent Badge */}
                <div style={{
                  display: 'inline-block',
                  background: exam.bgColor,
                  color: exam.color,
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  marginBottom: '16px',
                  letterSpacing: '0.5px'
                }}>
                  {exam.accent}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    background: exam.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileText size={28} color={exam.color} />
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}>
                    {exam.title}
                  </h3>
                </div>

                <div style={{
                  fontSize: '0.938rem',
                  fontWeight: '600',
                  color: '#475569',
                  marginBottom: '8px'
                }}>
                  {exam.description}
                </div>
                
                <p style={{
                  fontSize: '0.875rem',
                  color: '#64748b',
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  {exam.details}
                </p>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '16px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#64748b'
                  }}>
                    120 Questions
                  </span>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#94a3b8',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}>
                    <Lock size={16} /> Locked
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DerivativesMockExamPackSalesPage;
